import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import foerderMonitor from '../../../services/FoerderMonitoringService';
import { masterSupabase } from '../../../lib/supabase-master';
import { 
  Bell, 
  TrendingUp, 
  Calendar, 
  ExternalLink, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Euro, 
  Zap,
  Filter,
  Search,
  Settings,
  Mail,
  Smartphone,
  Monitor,
  MessageSquare,
  RefreshCw
} from 'lucide-react';

const FoerderMonitoring = () => {
  const [updates, setUpdates] = useState([]);
  const [notifications, setNotifications] = useState({
    dashboard: true,
    email: true,
    push: false,
    whatsapp: false
  });
  const [filter, setFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [monitoringStatus, setMonitoringStatus] = useState(null);

  useEffect(() => {
    loadUpdates();
    checkMonitoringStatus();
  }, []);

  // Echte Updates aus der KORREKTEN Tabelle laden
  const loadUpdates = async () => {
    try {
      setIsLoading(true);
      
      // KORREKTE Tabelle: funding_items (nicht funding_updates!)
      const { data, error } = await masterSupabase
        .from('funding_items')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Fehler beim Laden der Updates:', error);
        // Fallback: Service verwenden
        const serviceData = await foerderMonitor.getTickerUpdates();
        setUpdates(serviceData || []);
      } else {
        // Daten für UI formatieren
        const formattedUpdates = data.map(item => ({
          id: item.id,
          title: item.title || 'Förder-Update',
          description: item.summary || item.description || 'Neue Förderinformation verfügbar',
          category: item.category || 'Fördermittel',
          priority: item.priority || 'mittel',
          type: item.type || 'news',
          impact: getImpactText(item.category, item.priority),
          amount: item.amount || 'Variabel',
          deadline: item.deadline || 'Offen',
          date: formatDate(item.pub_date || item.created_at),
          source: item.source || 'KfW/BAFA',
          link: item.link,
          clara_score: item.clara_score,
          clara_insight: item.clara_insight,
          is_new: item.is_new
        }));
        
        setUpdates(formattedUpdates);
        setLastUpdate(new Date().toLocaleString('de-DE'));
      }
    } catch (error) {
      console.error('Fehler beim Laden der Updates:', error);
      setUpdates([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Monitoring-Status prüfen
  const checkMonitoringStatus = async () => {
    try {
      // Prüfe ob Services laufen
      const { data, error } = await masterSupabase
        .from('funding_items')
        .select('created_at')
        .order('created_at', { ascending: false })
        .limit(1);

      if (data && data.length > 0) {
        const lastEntry = new Date(data[0].created_at);
        const now = new Date();
        const diffHours = (now - lastEntry) / (1000 * 60 * 60);
        
        setMonitoringStatus({
          status: diffHours < 2 ? 'aktiv' : 'verzögert',
          lastSync: lastEntry.toLocaleString('de-DE'),
          nextSync: new Date(now.getTime() + 30 * 60 * 1000).toLocaleString('de-DE')
        });
      }
    } catch (error) {
      console.error('Fehler beim Prüfen des Monitoring-Status:', error);
      setMonitoringStatus({
        status: 'unbekannt',
        lastSync: 'Unbekannt',
        nextSync: 'Unbekannt'
      });
    }
  };

  // Impact-Text basierend auf Kategorie und Priorität
  const getImpactText = (category, priority) => {
    if (category === 'Fördermittel' && priority === 'hoch') {
      return 'Hohe Auswirkung auf Portfolio-ROI';
    } else if (category === 'Zinsen') {
      return 'Refinanzierungs-Potenzial prüfen';
    } else if (category === 'Energie') {
      return 'Sanierungsmaßnahmen optimieren';
    }
    return 'Moderate Auswirkung';
  };

  // Datum formatieren
  const formatDate = (dateString) => {
    if (!dateString) return 'Unbekannt';
    try {
      return new Date(dateString).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Updates manuell aktualisieren
  const handleRefresh = async () => {
    await loadUpdates();
    await checkMonitoringStatus();
  };

  // Filter anwenden
  const filteredUpdates = updates.filter(update => {
    if (filter === 'all') return true;
    if (filter === 'kritisch') return update.priority === 'kritisch';
    if (filter === 'neu') return update.is_new;
    if (filter === 'kfw') return update.source && update.source.toLowerCase().includes('kfw');
    if (filter === 'bafa') return update.source && update.source.toLowerCase().includes('bafa');
    if (filter === 'kommunal') return update.category === 'kommunal';
    return true;
  });

  // Prioritäts-Farben
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'kritisch': return 'bg-red-100 text-red-800';
      case 'hoch': return 'bg-orange-100 text-orange-800';
      case 'mittel': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Type-Icons
  const getTypeIcon = (type) => {
    switch (type) {
      case 'deadline': return <AlertTriangle className="h-4 w-4" />;
      case 'new_program': return <Zap className="h-4 w-4" />;
      case 'rate_change': return <TrendingUp className="h-4 w-4" />;
      default: return <Bell className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header mit Status */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Förder-Monitoring
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Automatische Überwachung von Förderprogrammen und Zinsentwicklungen
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          {monitoringStatus && (
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${
                monitoringStatus.status === 'aktiv' ? 'bg-green-500' : 'bg-yellow-500'
              }`}></div>
              <span className="text-sm text-gray-600">
                {monitoringStatus.status === 'aktiv' ? 'Live' : 'Verzögert'}
              </span>
            </div>
          )}
          
          <Button 
            onClick={handleRefresh} 
            disabled={isLoading}
            size="sm"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            {isLoading ? 'Lädt...' : 'Aktualisieren'}
          </Button>
        </div>
      </div>

      {/* Monitoring Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Monitor className="h-5 w-5" />
            <span>Monitoring Status</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <span className="text-sm font-medium text-gray-500">Status:</span>
              <p className="text-lg font-semibold text-green-600">
                {monitoringStatus?.status || 'Wird geladen...'}
              </p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">Letzte Aktualisierung:</span>
              <p className="text-sm text-gray-900 dark:text-white">
                {lastUpdate || 'Wird geladen...'}
              </p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">Nächste Prüfung:</span>
              <p className="text-sm text-gray-900 dark:text-white">
                {monitoringStatus?.nextSync || 'Wird berechnet...'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Benachrichtigungseinstellungen */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="h-5 w-5" />
            <span>Benachrichtigungen</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center space-x-2">
              <input 
                type="checkbox" 
                checked={notifications.dashboard}
                onChange={(e) => setNotifications(prev => ({...prev, dashboard: e.target.checked}))}
                className="rounded"
              />
              <Monitor className="h-4 w-4 text-blue-600" />
              <span className="text-sm">Dashboard</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <input 
                type="checkbox" 
                checked={notifications.email}
                onChange={(e) => setNotifications(prev => ({...prev, email: e.target.checked}))}
                className="rounded"
              />
              <Mail className="h-4 w-4 text-red-600" />
              <span className="text-sm">E-Mail</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <input 
                type="checkbox" 
                checked={notifications.push}
                onChange={(e) => setNotifications(prev => ({...prev, push: e.target.checked}))}
                className="rounded"
              />
              <Smartphone className="h-4 w-4 text-purple-600" />
              <span className="text-sm">Push</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <input 
                type="checkbox" 
                checked={notifications.whatsapp}
                onChange={(e) => setNotifications(prev => ({...prev, whatsapp: e.target.checked}))}
                className="rounded"
              />
              <MessageSquare className="h-4 w-4 text-green-500" />
              <span className="text-sm">WhatsApp</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filter */}
      <div className="flex items-center space-x-2">
        <Filter className="h-4 w-4 text-gray-500" />
        <div className="flex space-x-2">
          {['all', 'kritisch', 'neu', 'kfw', 'bafa', 'kommunal'].map(filterOption => (
            <Button
              key={filterOption}
              variant={filter === filterOption ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(filterOption)}
            >
              {filterOption === 'all' ? 'Alle' : 
               filterOption === 'kritisch' ? 'Kritisch' :
               filterOption === 'neu' ? 'Neue Programme' :
               filterOption.toUpperCase()}
            </Button>
          ))}
        </div>
      </div>

      {/* Updates Liste */}
      <div className="space-y-4">
        {isLoading ? (
          <Card>
            <CardContent className="p-8 text-center">
              <RefreshCw className="h-8 w-8 text-blue-500 mx-auto mb-4 animate-spin" />
              <p className="text-gray-600">Updates werden geladen...</p>
            </CardContent>
          </Card>
        ) : (
          filteredUpdates.map(update => (
            <Card key={update.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className={`p-1 rounded ${getPriorityColor(update.priority)}`}>
                        {getTypeIcon(update.type)}
                      </div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {update.title}
                      </h3>
                      <Badge variant="outline">{update.category}</Badge>
                      <Badge className={getPriorityColor(update.priority)}>
                        {update.priority}
                      </Badge>
                      {update.clara_score && (
                        <Badge className="bg-blue-100 text-blue-800">
                          Clara-Score: {update.clara_score}%
                        </Badge>
                      )}
                    </div>
                    
                    <p className="text-gray-600 dark:text-gray-400 mb-3">
                      {update.description}
                    </p>
                    
                    {update.clara_insight && (
                      <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mb-3">
                        <p className="text-sm text-blue-800">
                          <strong>Clara-Insight:</strong> {update.clara_insight}
                        </p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                      <div>
                        <span className="text-sm font-medium text-gray-500">Auswirkung:</span>
                        <p className="text-sm text-gray-900 dark:text-white">{update.impact}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">Betrag:</span>
                        <p className="text-sm font-bold text-green-600">{update.amount}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">Deadline:</span>
                        <p className="text-sm text-gray-900 dark:text-white">{update.deadline}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span className="flex items-center">
                          <Calendar className="h-3 w-3 mr-1" />
                          {update.date}
                        </span>
                        <span>Quelle: {update.source}</span>
                      </div>
                      
                      {update.link && update.link !== '#' && (
                        <Button variant="outline" size="sm" asChild>
                          <a href={update.link} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-3 w-3 mr-1" />
                            Details
                          </a>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {filteredUpdates.length === 0 && !isLoading && (
        <Card>
          <CardContent className="p-8 text-center">
            <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Keine Updates gefunden
            </h3>
            <p className="text-gray-500 mb-4">
              {filter === 'all' 
                ? 'Aktuell sind keine neuen Förderprogramme verfügbar.' 
                : `Keine Updates für Filter "${filter}" gefunden.`
              }
            </p>
            <Button onClick={handleRefresh} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Erneut prüfen
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FoerderMonitoring;

